// Toggle a `.rev()` effect on a voice. If `.rev()` is already in the
// chain, remove it; otherwise append. Reverse is its own inverse — two
// applications return the original — so a single command toggles the
// state. Works on both voice.s and voice.note.

import { newBlockId } from '../blocks/types';
import type { EditRecipe } from '../blocks/editContext';
import type { Transform } from './types';

export const reverseTransform: Transform = {
  id: 'reverse',
  category: 'rhythm',
  verb: 'Reverse',
  description: 'Toggle .rev() on the voice. Reapply to undo.',
  apply({ song, voiceName }) {
    const stmt = song.statements.find(
      (s) => s.kind === 'const' && s.name === voiceName,
    );
    if (!stmt || stmt.kind !== 'const') {
      return { ok: false, reason: `no const named "${voiceName}"` };
    }
    const expr = stmt.expr;
    if (expr.kind !== 'voice.s' && expr.kind !== 'voice.note') {
      return {
        ok: false,
        reason: `"${voiceName}" is not a voice (kind: ${expr.kind})`,
      };
    }
    const hasRev = expr.effects.some((e) => e.kind === 'rev');
    const recipe: EditRecipe = (draft) => {
      const target = draft.statements.find(
        (s) => s.kind === 'const' && s.name === voiceName,
      );
      if (!target || target.kind !== 'const') return;
      const ex = target.expr;
      if (ex.kind !== 'voice.s' && ex.kind !== 'voice.note') return;
      if (hasRev) {
        ex.effects = ex.effects.filter((e) => e.kind !== 'rev');
      } else {
        ex.effects.push({ id: newBlockId(), kind: 'rev', args: [] });
      }
    };
    return {
      ok: true,
      recipe,
      summary: `${hasRev ? 'unreverse' : 'reverse'} ${voiceName}`,
    };
  },
};
